import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FilterList, SearchConditions, StatWidgetInfo, StatsInfoMap } from './dashboard.model';

@Injectable({ providedIn: 'root' })
export class DashboardService {
    public filterList$: BehaviorSubject<FilterList> = new BehaviorSubject<FilterList>(
        {} as FilterList
    );
    public searchConditions: BehaviorSubject<SearchConditions> =
        new BehaviorSubject<SearchConditions>(new SearchConditions());
    public statsMap$: BehaviorSubject<StatsInfoMap> = new BehaviorSubject<StatsInfoMap>(null);
    public clickedLoginHandle: any;
    public requestToRedirect$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    public dashboardAttached$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    private statsMap: {
        activated: StatWidgetInfo;
        auTrend: StatWidgetInfo;
        stream: StatWidgetInfo;
        question: StatWidgetInfo;
    };

    constructor() {
        this.statsMap = {
            activated: {
                type: '',
                title: 'Activated<br/>Users',
                icon: null,
                count: 0,
                totalCount: 100,
                link: null,
                data: null,
                graphType: 'progress',
                textColor: '#1d896f',
                bgColor: '#cafff3',
                path: null,
                tabType: null,
                dataAvailable: false
            },
            auTrend: {
                type: '',
                title: 'Active Users',
                icon: null,
                count: 0,
                totalCount: 100,
                link: null,
                data: null,
                graphType: 'progress',
                textColor: '#21889b',
                bgColor: '#d1f8ff',
                path: null,
                tabType: null,
                dataAvailable: false
            },
            stream: {
                type: 'stream',
                title: 'Currently<br/>Streaming',
                icon: 'assets/images/dashboard-stream-info.png',
                count: null,
                dataAvailable: false,
                totalCount: null,
                link: null,
                data: null,
                graphType: 'bar',
                textColor: '#2c67bd',
                bgColor: '#DAEAFF',
                path: 'streamAnalytics',
                tabType: null
            },
            question: {
                type: 'question',
                title: 'Question<br/>Responses',
                icon: 'assets/images/dashboard-question-info.png',
                count: null,
                dataAvailable: false,
                totalCount: null,
                link: null,
                data: null,
                graphType: 'bar',
                textColor: '#1da8ff',
                bgColor: '#D2EDFE',
                path: 'questionAnalytics',
                tabType: null
            }
        };
    }

    clear() {
        this.searchConditions.next(new SearchConditions());
    }

    updateFilters(widgetName, filters: { [index: string]: any }) {
        if (widgetName === 'filter') {
            this.filterList$.next(filters as FilterList);
        }
        const newConditions = this.searchConditions.value;
        newConditions.filters = filters;
        this.searchConditions.next(newConditions);
        sessionStorage.setItem('dashboard_' + widgetName, JSON.stringify(newConditions));
    }

    setStatsData(widgetName, data, tabType, hasData) {
        if (widgetName === 'user_trend') {
            this.statsMap.activated.count = data.active_count;
            this.statsMap.activated.totalCount = data.total_count;
            this.statsMap.activated.dataAvailable = hasData;

            this.statsMap.auTrend.count = tabType === 'AU7' ? data.au7 : data.au30;
            this.statsMap.auTrend.title =
                tabType === 'AU7' ? 'Active Users<br/>(7 Days)' : 'Active Users<br/>(30 Days)';
            this.statsMap.auTrend.totalCount = data.active_count;
            this.statsMap.auTrend.tabType = tabType;
            this.statsMap.auTrend.dataAvailable = hasData;
            this.statsMap$.next(this.statsMap);
        } else if (
            (widgetName === 'stream_analytics' && tabType.toLowerCase() === 'completion') ||
            widgetName === 'question_analytics'
        ) {
            const updatedData = [
                { value: 0, label: '0-25' },
                { value: 0, label: '25-50' },
                { value: 0, label: '50-75' },
                { value: 0, label: '75-100' }
            ];
            Object.keys(data.data).forEach((key, _value) => {
                updatedData[0].value += data.data[key][0];
                updatedData[1].value += data.data[key][1];
                updatedData[2].value += data.data[key][2];
                updatedData[3].value += data.data[key][3];
            });

            const mapKey = widgetName === 'stream_analytics' ? 'stream' : 'question';
            this.statsMap[mapKey].data = updatedData;
            this.statsMap[mapKey].count = data.currentlyStreaming;
            this.statsMap[mapKey].tabType = tabType;
            this.statsMap[mapKey].dataAvailable = hasData;
            this.statsMap$.next(this.statsMap);
        }
    }

    clearDashboardSession() {
        sessionStorage.removeItem('dashboard_user_trend');
        sessionStorage.removeItem('dashboard_stream_analytics');
        sessionStorage.removeItem('dashboard_question_analytics');
        sessionStorage.removeItem('dashboard_skill_map_analytics');
    }

    clearAnalyticsSession() {
        // Clears SessionStorage for Analytics pages
        sessionStorage.removeItem('analytics_userList');
        sessionStorage.removeItem('analytics_streamList');
        sessionStorage.removeItem('analytics_streamUsageList');
        sessionStorage.removeItem('analytics_quesAssignmentList');
        sessionStorage.removeItem('analytics_questionList');

        // sessionStorage.removeItem('assessmentList');
        sessionStorage.removeItem('questionList');
        // sessionStorage.removeItem('createdByUsers');
        sessionStorage.removeItem('streamList');
    }

    // Utility Function to select roleCombinations. Used from analytics pages as well
    selectRoleCombinations(allCombinations, selectedList, searchedCombination) {
        let newCombinationList = [];
        let allRolesSelected = false;
        if (!searchedCombination.length) {
            const availableGroups = allCombinations
                .map(v => v.roleId)
                .filter((v, i, a) => a.indexOf(v) === i)
                .map(x => {
                    return {
                        role_id: x,
                        agency_id: null,
                        bunit: null
                    };
                });
            newCombinationList = [...selectedList, ...availableGroups];
            allRolesSelected = true;
        } else {
            const checkForRoles = this.checkForAllRoleGroups(
                allCombinations,
                searchedCombination.map(x => x.value)
            );
            allRolesSelected = checkForRoles.allRolesSelected;
            newCombinationList = [...selectedList, ...checkForRoles.selectedItemList].filter(
                (v, i, a) => a.indexOf(v) === i
            );
        }
        return { list: newCombinationList, allRolesSelected };
    }

    checkForAllCountryRoleSelection(
        allCountries,
        selectedCountries,
        allRoleCombinations,
        selectedRoleCombination
    ) {
        const allCountriesSelected =
            selectedCountries && selectedCountries.length
                ? allCountries.length === selectedCountries.length
                    ? true
                    : false
                : false;
        const allRolesSelected = this.checkForAllRoleGroups(
            allRoleCombinations,
            selectedRoleCombination
        ).allRolesSelected;
        return {
            allCountriesSelected,
            allRolesSelected
        };
    }

    checkForAllRoleGroups(allCombinations, checkFromList: [{ role_id; agency_id; bunit }]) {
        if (checkFromList?.length) {
            const searchedGroups = checkFromList.filter(v => !v.agency_id && !v.bunit);
            const selectedItemList = [];
            const selectedGroups = [];
            searchedGroups.forEach(group => {
                const groupItems = allCombinations
                    .map(v => v.value)
                    .filter(v => v.role_id === group.role_id);
                const searchedItems = checkFromList.filter(
                    x => x.role_id === group.role_id && (x.agency_id || x.bunit)
                );
                selectedItemList.push(...searchedItems);
                if (groupItems.length === searchedItems.length) {
                    // Include Group
                    selectedGroups.push(group);
                }
            });

            const availableGroups = allCombinations
                .map(v => v.roleId)
                .filter((v, i, a) => a.indexOf(v) === i) // De-dup!
                .map(x => {
                    return {
                        role_id: x,
                        agency_id: null,
                        bunit: null
                    };
                });

            return {
                allRolesSelected: selectedGroups.length === availableGroups.length ? true : false,
                selectedItemList
            };
        } else {
            return { allRolesSelected: false, selectedItemList: [] };
        }
    }

    getScoreColor(score: string | number) {
        const value =
            typeof score === 'string' ? (score === '--' ? -1 : parseInt(score, 10)) : score;
        const bucket = Math.floor(value / 25);
        const colors = ['#ff3333', '#f78c3b', '#ffc933', '#a1c91d', '#a1c91d'];
        return colors[bucket] || '#aaa';
    }
}
